import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import LandingLayout from 'layouts/LandingLayout/LandingLayout';
import Header from 'Components/Header/Header';
import Footer from 'Components/LandingPage/Footer/Footer';

import {
  TOOL_CATEGORIES,
  MCP_ENDPOINT,
  TOOL_COUNT,
  RESOURCE_COUNT,
} from 'data/mcpTools';

import './MCPLanding.scss';

const SETUP_ID = 'connect';

/**
 * Number of tools behind a capability card, derived from the generated data.
 * Most cards map 1:1 onto a category; a card may instead name its tools
 * explicitly when the capability spans categories (publishing does).
 */
const countIn = (cap) => {
  if (cap.toolNames) {
    const names = new Set(cap.toolNames);
    return TOOL_CATEGORIES.reduce(
      (n, c) => n + c.tools.filter((t) => names.has(t.name)).length,
      0
    );
  }
  const category = TOOL_CATEGORIES.find((c) => c.id === cap.id);
  return category ? category.tools.length : 0;
};

/** First sentence of a tool description, so cards stay one line-ish. */
const firstSentence = (text = '') => {
  const trimmed = text.replace(/\s+/g, ' ').trim();
  const stop = trimmed.search(/[.!?](\s|$)/);
  const sentence = stop === -1 ? trimmed : trimmed.slice(0, stop + 1);
  return sentence.length > 150 ? `${sentence.slice(0, 147)}…` : sentence;
};

const CAPABILITIES = [
  {
    id: 'content',
    title: 'Drafting in your brand voice',
    body:
      'Your agent writes with your voice DNA, guidelines and audience personas already loaded, so the first draft sounds like you instead of like a generic model.',
  },
  {
    id: 'documents',
    title: 'Document workflows end to end',
    body:
      'Create, read, revise and publish Vocable documents from the conversation — including a content health check before anything goes out.',
  },
  {
    id: 'planning',
    title: 'Campaign and brief planning',
    body:
      'Spin up campaigns and briefs, generate headline options, and keep the plan in Vocable where your team already works.',
  },
  {
    id: 'intelligence',
    title: 'Brand intelligence and search',
    body:
      'Semantic search across your own content library, find similar pieces, run keyword research and read back the insights Vocable has learned about your brand.',
  },
  {
    id: 'training',
    title: 'Voice DNA training',
    body:
      'Point the agent at your blog or social posts, import samples, and run the voice analysis that teaches Vocable how you sound.',
  },
  {
    id: 'publishing',
    toolNames: ['publish_document', 'get_publish_settings', 'list_integrations'],
    title: 'Publishing to your channels',
    body:
      'Send a finished document straight to a connected channel — WordPress, Webflow, Wix, LinkedIn, X or Facebook — with story details, SEO fields and scheduling set from the conversation. The agent asks you to confirm before anything goes live.',
  },
  {
    id: 'collaboration',
    title: 'Collaboration',
    body:
      'Share brands, documents and campaigns with teammates, and see who already has access.',
  },
];

const STEPS = [
  {
    title: 'Add the connector in Claude',
    body:
      'In Claude on the web, open Settings → Connectors → Add custom connector and paste the Vocable MCP URL above. Claude discovers the tools automatically.',
  },
  {
    title: 'Sign in and approve access',
    body:
      'Claude sends you to Vocable to sign in with your existing account and approve the connection. Nothing is shared until you approve, and the connector only ever acts as you.',
  },
  {
    title: 'Start prompting',
    body:
      'That is the setup done. In your first session you tell the agent which brand to work on — the tools are scoped to one brand at a time — and then just describe the work you want.',
  },
];

const CLIENTS = [
  {
    name: 'Claude (web)',
    status: 'supported',
    statusLabel: 'Supported today',
    note: 'Add it once under Settings → Connectors and start prompting.',
  },
  {
    name: 'Claude Desktop',
    status: 'supported',
    statusLabel: 'Supported today',
    note:
      'Connectors are brokered by your Claude account, so the one you added on the web is already there — no separate setup.',
  },
  {
    name: 'Claude Code',
    status: 'soon',
    statusLabel: 'Coming soon',
    note:
      'It signs in over a local callback address that is not registered with the connector yet.',
  },
  {
    name: 'Cursor',
    status: 'soon',
    statusLabel: 'Coming soon',
    note: 'Its sign-in callback is not registered with the connector yet.',
  },
];

const FAQS = [
  {
    q: 'Does the connector cost extra?',
    a:
      'The connector is a new way into the Vocable account you already have. Work an agent does through it is the same work you would do in the app, and it counts against the same account limits. If you are unsure how your current plan is metered, check your plan page or ask us before you wire it into anything automated.',
  },
  {
    q: 'What can the agent actually see?',
    a:
      'Only what you approve, and only as you. Once connected, the agent can reach the brand you have made active — its profile, voice DNA, guidelines, personas, content samples, documents, briefs and campaigns — through the tools listed on this page. It cannot see brands or documents your Vocable account cannot see.',
  },
  {
    q: 'Which plan do I need?',
    a:
      'The connector uses your existing Vocable sign-in and your account\'s usual entitlements — if a feature is available to you in the app, the matching tool is available to your agent, and if it is not, the tool will tell the agent so. We are not going to quote you a tier here that might be wrong for your account; your plan page is the accurate answer.',
  },
  {
    q: 'Is this the same as the Vocable API?',
    a:
      'No. The API is for code you write. The MCP connector is for AI agents: it describes each tool, its arguments and its results in a way a model can use directly, so you can ask for an outcome in plain language instead of wiring up requests.',
  },
  {
    q: 'How do I disconnect?',
    a:
      'Remove the connector in your Claude settings, where you added it. Access ends there — no content is deleted from Vocable, and you can reconnect later by adding the same URL again.',
  },
];

const CopyEndpoint = () => {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const handleCopy = useCallback(async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(MCP_ENDPOINT);
      } else {
        return;
      }
      setCopied(true);
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      setCopied(false);
    }
  }, []);

  return (
    <div className="mcp-landing__chip">
      <span className="mcp-landing__chip-label">Connector URL</span>
      <code className="mcp-landing__chip-code">{MCP_ENDPOINT}</code>
      <button
        type="button"
        className="mcp-landing__chip-btn"
        onClick={handleCopy}
        aria-label={`Copy the Vocable MCP connector URL ${MCP_ENDPOINT}`}
      >
        {copied ? 'Copied' : 'Copy'}
      </button>
      <span className="mcp-landing__sr-only" role="status" aria-live="polite">
        {copied ? 'Connector URL copied to clipboard' : ''}
      </span>
    </div>
  );
};

const MCPLanding = () => (
  <LandingLayout>
    <div className="mcp-landing">
      <Header />

      <main className="mcp-landing__main">
        {/* 1. Hero */}
        <section className="mcp-landing__hero" aria-labelledby="mcp-hero-title">
          <div className="mcp-landing__inner">
            <div className="mcp-landing__topbar">
              <Link className="mcp-landing__doclink" to="/docs/mcp/overview">
                View documentation
                <span aria-hidden="true">→</span>
              </Link>
            </div>

            <p className="mcp-landing__eyebrow">
              Model Context Protocol connector
            </p>
            <h1 id="mcp-hero-title" className="mcp-landing__h1">
              Your brand&apos;s voice, inside any AI agent
            </h1>
            <p className="mcp-landing__lede">
              The Vocable MCP connector plugs your brand into Claude. Your agent
              can research your market, search your own content library, draft in
              your voice DNA and publish the result — without you leaving the
              conversation or re-explaining who you are.
            </p>

            <CopyEndpoint />

            <div className="mcp-landing__cta-row">
              <a className="mcp-landing__btn" href={`#${SETUP_ID}`}>
                Connect to Claude
              </a>
              <Link
                className="mcp-landing__btn mcp-landing__btn--ghost"
                to="/docs/mcp/overview"
              >
                Read the docs
              </Link>
            </div>

            <ul className="mcp-landing__facts">
              <li>
                <strong>{TOOL_COUNT}</strong> tools
              </li>
              <li>
                <strong>{TOOL_CATEGORIES.length}</strong> categories
              </li>
              <li>
                <strong>{RESOURCE_COUNT}</strong> brand resources
              </li>
            </ul>
          </div>
        </section>

        {/* 2. What your agent can do */}
        <section
          className="mcp-landing__section"
          aria-labelledby="mcp-capabilities-title"
        >
          <div className="mcp-landing__inner">
            <h2 id="mcp-capabilities-title" className="mcp-landing__h2">
              What your agent can do
            </h2>
            <p className="mcp-landing__sub">
              {TOOL_COUNT} tools, grouped into the jobs you actually ask for.
            </p>

            <ul className="mcp-landing__grid mcp-landing__grid--cards">
              {CAPABILITIES.map((cap) => (
                <li className="mcp-landing__card" key={cap.id}>
                  <h3 className="mcp-landing__h3">{cap.title}</h3>
                  <p>{cap.body}</p>
                  <p className="mcp-landing__card-meta">
                    {countIn(cap)} {countIn(cap) === 1 ? 'tool' : 'tools'}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* 3. How it works */}
        <section
          className="mcp-landing__section mcp-landing__section--tint"
          id={SETUP_ID}
          aria-labelledby="mcp-setup-title"
        >
          <div className="mcp-landing__inner">
            <h2 id="mcp-setup-title" className="mcp-landing__h2">
              How it works
            </h2>
            <p className="mcp-landing__sub">
              Two steps to connect, then you are working. No API key, nothing to
              install.{' '}
              <Link to="/docs/mcp/quickstart">
                The full walkthrough is in the docs
              </Link>
              .
            </p>

            <div className="mcp-landing__chip-wrap">
              <CopyEndpoint />
            </div>

            <ol className="mcp-landing__steps">
              {STEPS.map((step, i) => (
                <li className="mcp-landing__step" key={step.title}>
                  <span className="mcp-landing__step-num" aria-hidden="true">
                    {i + 1}
                  </span>
                  <div>
                    <h3 className="mcp-landing__h3">{step.title}</h3>
                    <p>{step.body}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* 4. Tool overview */}
        <section
          className="mcp-landing__section"
          aria-labelledby="mcp-tools-title"
        >
          <div className="mcp-landing__inner">
            <h2 id="mcp-tools-title" className="mcp-landing__h2">
              Every tool in the connector
            </h2>
            <p className="mcp-landing__sub">
              Generated from the live server, so this list is what your agent
              really sees.
            </p>

            <div className="mcp-landing__grid mcp-landing__grid--tools">
              {TOOL_CATEGORIES.map((category) => (
                <section
                  className="mcp-landing__toolcat"
                  key={category.id}
                  aria-labelledby={`mcp-cat-${category.id}`}
                >
                  <h3 id={`mcp-cat-${category.id}`} className="mcp-landing__h3">
                    {category.label}
                    <span className="mcp-landing__pill">
                      {category.tools.length}
                    </span>
                  </h3>
                  <p className="mcp-landing__toolcat-blurb">{category.blurb}</p>
                  <dl className="mcp-landing__toollist">
                    {category.tools.map((tool) => (
                      <React.Fragment key={tool.name}>
                        <dt>
                          <code>{tool.name}</code>
                        </dt>
                        <dd>{firstSentence(tool.description)}</dd>
                      </React.Fragment>
                    ))}
                  </dl>
                </section>
              ))}
            </div>

            <p className="mcp-landing__more">
              <Link to="/docs/mcp/tools">
                Full tool reference, with every argument →
              </Link>
            </p>
          </div>
        </section>

        {/* 5. Client support */}
        <section
          className="mcp-landing__section mcp-landing__section--tint"
          aria-labelledby="mcp-clients-title"
        >
          <div className="mcp-landing__inner">
            <h2 id="mcp-clients-title" className="mcp-landing__h2">
              Where it works
            </h2>
            <p className="mcp-landing__sub">
              The connector works in Claude on the web and in Claude Desktop
              &mdash; connectors are brokered through your Claude account, so one
              connection covers both. The editor and terminal clients sign in
              over a local callback address that the connector does not accept
              yet; we would rather say so than let you find out mid-setup.
            </p>

            <ul className="mcp-landing__clients">
              {CLIENTS.map((client) => (
                <li
                  className={`mcp-landing__client mcp-landing__client--${client.status}`}
                  key={client.name}
                >
                  <h3 className="mcp-landing__h3">{client.name}</h3>
                  <p className="mcp-landing__badge">{client.statusLabel}</p>
                  <p>{client.note}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* 6. FAQ */}
        <section className="mcp-landing__section" aria-labelledby="mcp-faq-title">
          <div className="mcp-landing__inner mcp-landing__inner--narrow">
            <h2 id="mcp-faq-title" className="mcp-landing__h2">
              Questions
            </h2>

            <div className="mcp-landing__faq">
              {FAQS.map((item) => (
                <details className="mcp-landing__faq-item" key={item.q}>
                  <summary>
                    <span className="mcp-landing__faq-q">{item.q}</span>
                    <span className="mcp-landing__faq-icon" aria-hidden="true" />
                  </summary>
                  <div className="mcp-landing__faq-a">
                    <p>{item.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* 7. Closing CTA */}
        <section
          className="mcp-landing__section mcp-landing__closing"
          aria-labelledby="mcp-closing-title"
        >
          <div className="mcp-landing__inner mcp-landing__inner--narrow">
            <h2 id="mcp-closing-title" className="mcp-landing__h2">
              Give your agent your brand
            </h2>
            <p className="mcp-landing__sub">
              The docs walk through adding the connector and approving access,
              then what to do in your first session.
            </p>
            <div className="mcp-landing__cta-row mcp-landing__cta-row--center">
              <Link className="mcp-landing__btn" to="/docs/mcp/overview">
                Read the docs
              </Link>
              <Link
                className="mcp-landing__btn mcp-landing__btn--ghost"
                to="/docs/mcp/tools"
              >
                Browse the tools
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  </LandingLayout>
);

export default MCPLanding;
